import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Own submissions (BM or ZM viewing their own uploads)
  submissions: [],
  submissionsLoading: false,
  submissionsError: null,

  // Review lists: BM submissions for ZM, or ZM submissions for NSM
  reviewList: [],
  reviewListLoading: false,
  reviewListError: null,

  // Feedback for a viewed submission
  feedback: [],
  feedbackLoading: false,
  feedbackError: null,

  // Compliance / due dates
  complianceStatus: null,
  complianceLoading: false,
  complianceError: null,

  // In-app notifications
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,

  // Upload / replace operation state
  uploadLoading: false,
  uploadError: null,

  // Feedback submit state
  submitFeedbackLoading: false,
  submitFeedbackError: null,
};

const jcSlice = createSlice({
  name: "jc",
  initialState,
  reducers: {
    // ── Submissions ──────────────────────────────
    fetchSubmissionsStart(state) {
      state.submissionsLoading = true;
      state.submissionsError = null;
    },
    fetchSubmissionsSuccess(state, { payload }) {
      state.submissionsLoading = false;
      state.submissions = payload;
    },
    fetchSubmissionsFailure(state, { payload }) {
      state.submissionsLoading = false;
      state.submissionsError = payload;
    },

    // ── Review list ──────────────────────────────
    fetchReviewListStart(state) {
      state.reviewListLoading = true;
      state.reviewListError = null;
    },
    fetchReviewListSuccess(state, { payload }) {
      state.reviewListLoading = false;
      state.reviewList = payload;
    },
    fetchReviewListFailure(state, { payload }) {
      state.reviewListLoading = false;
      state.reviewListError = payload;
    },

    // ── Feedback ─────────────────────────────────
    fetchFeedbackStart(state) {
      state.feedbackLoading = true;
      state.feedbackError = null;
    },
    fetchFeedbackSuccess(state, { payload }) {
      state.feedbackLoading = false;
      state.feedback = payload;
    },
    fetchFeedbackFailure(state, { payload }) {
      state.feedbackLoading = false;
      state.feedbackError = payload;
    },

    // ── Compliance ────────────────────────────────
    fetchComplianceStart(state) {
      state.complianceLoading = true;
      state.complianceError = null;
    },
    fetchComplianceSuccess(state, { payload }) {
      state.complianceLoading = false;
      state.complianceStatus = payload;
    },
    fetchComplianceFailure(state, { payload }) {
      state.complianceLoading = false;
      state.complianceError = payload;
    },

    // ── Notifications ─────────────────────────────
    fetchNotificationsStart(state) {
      state.notificationsLoading = true;
      state.notificationsError = null;
    },
    fetchNotificationsSuccess(state, { payload }) {
      state.notificationsLoading = false;
      state.notifications = payload;
    },
    fetchNotificationsFailure(state, { payload }) {
      state.notificationsLoading = false;
      state.notificationsError = payload;
    },
    markNotificationReadLocal(state, { payload: id }) {
      const n = state.notifications.find((n) => n.id === id);
      if (n) n.is_read = true;
    },
    markAllNotificationsReadLocal(state) {
      state.notifications.forEach((n) => {
        n.is_read = true;
      });
    },

    // ── Upload / Replace ──────────────────────────
    uploadStart(state) {
      state.uploadLoading = true;
      state.uploadError = null;
    },
    uploadSuccess(state, { payload }) {
      state.uploadLoading = false;
      // Prepend the newly created/replaced submission to the list
      state.submissions = [payload, ...state.submissions.filter((s) => s.id !== payload.id)];
    },
    uploadFailure(state, { payload }) {
      state.uploadLoading = false;
      state.uploadError = payload;
    },

    // ── Submit Feedback ───────────────────────────
    submitFeedbackStart(state) {
      state.submitFeedbackLoading = true;
      state.submitFeedbackError = null;
    },
    submitFeedbackSuccess(state, { payload }) {
      state.submitFeedbackLoading = false;
      // Update the review list row with the new feedback
      state.reviewList = state.reviewList.map((row) =>
        row.id === payload.submission_id
          ? {
              ...row,
              rating: payload.rating,
              remarks: payload.remarks,
              reviewer_name: payload.reviewer_name,
              reviewer_role: payload.reviewer_role,
              feedback_date: payload.feedback_date,
            }
          : row
      );
    },
    submitFeedbackFailure(state, { payload }) {
      state.submitFeedbackLoading = false;
      state.submitFeedbackError = payload;
    },

    // ── Reset ─────────────────────────────────────
    resetJcState() {
      return initialState;
    },
  },
});

export const {
  fetchSubmissionsStart, fetchSubmissionsSuccess, fetchSubmissionsFailure,
  fetchReviewListStart, fetchReviewListSuccess, fetchReviewListFailure,
  fetchFeedbackStart, fetchFeedbackSuccess, fetchFeedbackFailure,
  fetchComplianceStart, fetchComplianceSuccess, fetchComplianceFailure,
  fetchNotificationsStart, fetchNotificationsSuccess, fetchNotificationsFailure,
  markNotificationReadLocal, markAllNotificationsReadLocal,
  uploadStart, uploadSuccess, uploadFailure,
  submitFeedbackStart, submitFeedbackSuccess, submitFeedbackFailure,
  resetJcState,
} = jcSlice.actions;

export const jcReducer = jcSlice.reducer;
